import { Address, CustomerAddress } from '@bigcommerce/checkout-sdk';
import React, { memo, FunctionComponent, PureComponent, ReactNode, useState } from 'react';
import { connect } from 'react-redux';
import B3Checkout from '../b3checkout';

import { preventDefault } from '../common/dom';
import { TranslatedString } from '../locale';
import { DropdownTrigger } from '../ui/dropdown';

import isEqualAddress from './isEqualAddress';
import './AddressSelect.scss';
import StaticAddress from './StaticAddress';

import { AddressFilterInput } from '../b3checkout'

export interface AddressSelectProps {
    addresses: CustomerAddress[];
    selectedAddress?: Address;
    onSelectAddress(address: Address): void;
    onUseNewAddress(currentAddress?: Address): void;
}

interface B3Props {
    b3Company: any;
    isB3User: boolean;
}

class AddressSelect extends PureComponent<AddressSelectProps & B3Props> {
    render(): ReactNode {
        const {
            addresses,
            selectedAddress,
            ...rest
        } = this.props;

        return (
            <div className="form-field">
                <div className="dropdown--select" role="combobox">
                    <DropdownTrigger
                        dropdown={
                            <AddressSelectMenu
                                {...rest}
                                addresses={ addresses }
                                onSelectAddress={ this.handleSelectAddress }
                                onUseNewAddress={ this.handleUseNewAddress }
                                selectedAddress={ selectedAddress }
                            />
                        }
                    >
                        <AddressSelectButton
                            {...rest}
                            addresses={ addresses }
                            selectedAddress={ selectedAddress }
                        />
                    </DropdownTrigger>
                </div>
            </div>
        );
    }

    private handleSelectAddress: (newAddress: Address) => void = (newAddress: Address) => {
        const {
            onSelectAddress,
            selectedAddress,
        } = this.props;

        if (!isEqualAddress(selectedAddress, newAddress)) {
            onSelectAddress(newAddress);
        }
    };

    private handleUseNewAddress: () => void = () => {
        const {
            selectedAddress,
            onUseNewAddress,
        } = this.props;

        onUseNewAddress(selectedAddress);
    };
}

const AddressSelectMenu: FunctionComponent<AddressSelectProps & B3Props> = ({
    addresses,
    onSelectAddress,
    onUseNewAddress,
    selectedAddress,
    isB3User,
}) => {
    const [disPlayAddress, setDisPlayAddress] = useState(addresses)

    return (
        <ul
            className="dropdown-menu instrumentSelect-dropdownMenu"
            id="addressDropdown"
        >
            { isB3User ? 
                <AddressFilterInput 
                    onChange={(q: string) => {
                        setDisPlayAddress(B3Checkout.utils.util.addressesFilterHandler(q, addresses))
                    }}
                /> : 
                (<li className="dropdown-menu-item dropdown-menu-item--select">
                    <a href="#" onClick={ preventDefault(() => onUseNewAddress(selectedAddress)) }>
                        <TranslatedString id="address.enter_address_action" />
                    </a>
                </li>) 
            }
            { disPlayAddress.map(address => (
                <li
                    className="dropdown-menu-item dropdown-menu-item--select"
                    key={ address.id }
                >
                    <a href="#" onClick={ preventDefault(() => onSelectAddress(address)) }>
                        <StaticAddress address={ address } />
                    </a>
                </li>
            )) }
        </ul>
    )
};

type AddressSelectButtonProps = Pick<AddressSelectProps, 'selectedAddress' | 'addresses'>;

const AddressSelectButton: FunctionComponent<AddressSelectButtonProps & B3Props> = ({
    selectedAddress,
    isB3User,
}) => (
    <a
        className="button dropdown-button dropdown-toggle--select"
        href="#"
        id="addressToggle"
        onClick={ preventDefault() }
    >
        { selectedAddress ?
            <StaticAddress address={ selectedAddress } /> :
            isB3User 
            ? B3Checkout.locales.tips['b3.addresses.select']
            : <TranslatedString id="address.enter_address_action" /> 
        }
    </a>
);

const mapStateToProps = (state: { global: { isB3User: boolean }, company: any }) => {
    const {
        global: {
            isB3User
        },
        company: b3Company
    } = state;

    return {
        b3Company,
        isB3User,
    };
};

export default memo(connect(mapStateToProps)(AddressSelect));
